import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import Button from '../../../components/ui/Button';
import { Table } from '../../performance-operations/styles';
import { fetchBreakdown, metaOfBreakdown, usePanel } from '../../performance-operations/config';
import { TAB_TASKS, isRealKey, processHealthHref } from '../../../components/performance/processHealthLinks';
import { CellLink, ExpandRow } from '../styles';
import {
  TOP_UNITS, UNIT_CHART_MAX, UNIT_CODES, UNIT_SEGMENTS, unitRows,
} from '../config';

// Spec §7.2 — organizasyon bazlı durum çubukları: Tamamlandı / Bekliyor / Gecikti / Muaf (görev bazlı), gecikene göre azalan ilk 10;
// "Tüm Birimler" kartı aşağı doğru genişletir (iç kaydırma yok; UNIT_CHART_MAX'tan çok birimde tablo olarak). Dilim tıklaması →
// Görevler, o birim + o durum (§19/4); tablo görünümünde aynı iniş klavyeyle yapılır.
const ROW_HEIGHT = 30;

export default function UnitStatusPanel({ filters, onMeta }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const [table, setTable] = useState(false);
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchBreakdown(filters, UNIT_CODES, 'department'), [key], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('unitStatus', metaOfBreakdown(panel.data)); }, [panel.data, onMeta]);

  const all = (panel.data?.cells || []).filter((c) => c.key);
  const rows = unitRows(all, TOP_UNITS, expanded);
  const colors = {
    done: theme.colors.secondary.main,
    waiting: theme.colors.primary.main,
    overdue: theme.colors.auxiliar.danger,
    exempt: theme.colors.text.muted,
  };
  const data = rows.map(({ cell, segments }) => ({
    key: cell.key,
    name: cell.label || t('performance.operations.unassigned'),
    ...Object.fromEntries(segments.map((s) => [s.key, s.value])),
  }));
  const forcedTable = expanded && rows.length > UNIT_CHART_MAX;
  const asTable = table || forcedTable;
  const go = (unitKey, segment) => {
    if (!isRealKey(unitKey)) return;
    history.push(processHealthHref(filters, { tab: TAB_TASKS, departmentIds: [unitKey], task: segment.task }));
  };

  return (
    <PanelCard
      panelKey="unitStatus" title={t('performance.processHealth.panel.unitStatus')} span={7}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      tableToggle={!forcedTable} showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {asTable ? (
        <Table>
          <thead>
            <tr>
              <th>{t('performance.operations.col.unit')}</th>
              {UNIT_SEGMENTS.map((s) => <th key={s.key}>{t(`performance.processHealth.matrix.${s.key}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.key} data-row={d.key}>
                <td title={d.name}>{d.name}</td>
                {UNIT_SEGMENTS.map((s) => (
                  <td key={s.key}>
                    {d[s.key] > 0 && isRealKey(d.key)
                      ? <CellLink onClick={() => go(d.key, s)} data-cell={`${d.key}:${s.key}`}>{d[s.key]}</CellLink>
                      : d[s.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: Math.max(140, data.length * ROW_HEIGHT + 48) }}>
          <ResponsiveContainer>
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
              {UNIT_SEGMENTS.map((s) => (
                <Bar
                  key={s.key} dataKey={s.key} name={t(`performance.processHealth.matrix.${s.key}`)} stackId="units" fill={colors[s.key]}
                  cursor="pointer" onClick={(d) => go((d.payload || d).key, s)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {all.length > TOP_UNITS ? (
        <ExpandRow>
          <Button variant="ghost" onClick={() => setExpanded((v) => !v)} data-action="expand">
            {expanded ? t('performance.processHealth.panel.showLess') : t('performance.processHealth.panel.allUnits', { count: all.length })}
          </Button>
        </ExpandRow>
      ) : null}
    </PanelCard>
  );
}
